// Dashboard — which widgets a role gets and their figures, from two SECURITY
// DEFINER doors (20261015220000_dashboard.sql). The server decides everything:
// whether a widget answers (dashboard:view AND the role's row for it), whose
// rows it adds up (the role's scope) and whether a deal value comes back (the
// project's manager or pipeline:full, never under 'studio_no_money'). This file
// names the shapes and calls the doors.
import { supabase } from './supabase';
import type { ProjectState } from './projects';

export type WidgetKey =
  'project_tiles' | 'projects' | 'tasks_due' | 'week_time' | 'timer' | 'client_reports' | 'away';
export type DashboardScope = 'studio' | 'studio_no_money' | 'chain' | 'own';

export type MyDashboard = { scope: DashboardScope; widgets: WidgetKey[] };

/** Counts of the projects in scope. `deal_value` null: no deal value is the caller's to see. */
export type ProjectTiles = {
  in_flight: number;
  at_risk: number;
  blocked: number;
  due_soon: number;
  overdue: number;
  deal_value: number | null;
  deals: number;
};
export type DashProject = {
  id: string;
  code: string | null;
  name: string;
  client: string | null;
  state: ProjectState;
  progress: number;
  deadline: string | null;
  stage: string | null;
  stage_idx: number;
  deal_value: number | null;
  team: string[];
};
export type DueTask = {
  id: string;
  code: string | null;
  title: string;
  due_date: string;
  jira_status: string | null;
  project_id: string;
  project: string;
  project_code: string | null;
  /** Set when it is a Jira issue; a native work item opens in Hyprflow. */
  jira_issue_id: string | null;
  assignees: string[];
};
export type WeekTime = {
  week_start: string;
  people: number;
  seconds: number;
  billable_seconds: number;
  worked_seconds: number;
  projects: {
    project_id: string;
    name: string;
    code: string | null;
    seconds: number;
    billable_seconds: number;
  }[];
};
export type DigestReport = {
  id: string;
  project_id: string;
  project: string;
  code: string | null;
  report_date: string;
  status: 'draft' | 'approved' | 'sent';
  summary: string;
  can_review: boolean;
};
export type AwayRow = {
  profile_id: string;
  name: string | null;
  start_date: string;
  end_date: string;
  half_day: boolean;
};

export type WidgetData = {
  project_tiles: ProjectTiles;
  projects: DashProject[];
  tasks_due: DueTask[];
  week_time: WeekTime;
  timer: null;
  client_reports: DigestReport[];
  away: AwayRow[];
};

/** Whose figures these are, in the viewer's words. */
export const SCOPE_LABEL: Record<DashboardScope, string> = {
  studio: 'The whole studio',
  studio_no_money: 'The whole studio, without deal values',
  chain: 'You and the people who report to you',
  own: 'Your own work',
};
export const SCOPES = Object.keys(SCOPE_LABEL) as DashboardScope[];

/** The caller's dashboard: `null` none, `undefined` the read failed. */
export async function loadMyDashboard(): Promise<MyDashboard | null | undefined> {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('my_dashboard');
  if (error) return undefined;
  return (data as MyDashboard | null) ?? null;
}

/** One widget's figures: `null` not this caller's, `undefined` the read failed. */
export async function loadWidget<K extends WidgetKey>(
  key: K,
): Promise<WidgetData[K] | null | undefined> {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('dashboard_widget', { p_key: key });
  if (error) return undefined;
  return (data as WidgetData[K] | null) ?? null;
}
